import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  COMPANY_SELECTED_KEY,
  CUSTOMER_ID_KEY,
  TENANT_SOURCE_KEY,
  TenantSource,
  consumeTenantRevokedNotice,
} from '@/lib/auth/authSession'
import { Notify } from '@/lib/misc/Notify'
import { useLoadCompanyProfile } from '@/lib/queries/CompanyProfile.queries'

interface TenantContextType {
  tenantName?: string | null
  companyName?: string | null
  customerId?: string | null
  tenantCid?: string | null
  tenantSource?: TenantSource | null
  hasChosenCompany: boolean
  setCustomerIdHandler: (id: string, source?: TenantSource) => void
  removeCustomerIdHandler: (options?: { markChosen?: boolean }) => void
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

interface TenantProviderProps {
  children: ReactNode
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const initialCustomerId = localStorage.getItem(CUSTOMER_ID_KEY)
  const [customerId, setCustomerId] = useState<string | undefined>(
    initialCustomerId || undefined
  )
  const [hasChosenCompany, setHasChosenCompany] = useState(
    Boolean(localStorage.getItem(COMPANY_SELECTED_KEY))
  )
  const [tenantSource, setTenantSource] = useState<TenantSource | undefined>(
    (localStorage.getItem(TENANT_SOURCE_KEY) as TenantSource) || undefined
  )

  useEffect(() => {
    if (!consumeTenantRevokedNotice()) return

    Notify.warn(
      'You no longer have access to the company you were working in, so we switched you back.',
      'Company access removed'
    )
  }, [])

  const { data: company } = useLoadCompanyProfile(
    false,
    undefined,
    !!customerId
  )
  const { data: tenant } = useLoadCompanyProfile(true, customerId, !!customerId)

  const markChosenCompany = useCallback(() => {
    localStorage.setItem(COMPANY_SELECTED_KEY, 'true')
    setHasChosenCompany(true)
  }, [])

  const setCustomerIdHandler = useCallback(
    (id: string, source: TenantSource = 'impersonation') => {
      localStorage.setItem(CUSTOMER_ID_KEY, id)
      localStorage.setItem(TENANT_SOURCE_KEY, source)
      setCustomerId(id)
      setTenantSource(source)
      if (source === 'membership') markChosenCompany()
    },
    [markChosenCompany]
  )

  const removeCustomerIdHandler = useCallback(
    (options?: { markChosen?: boolean }) => {
      localStorage.removeItem(CUSTOMER_ID_KEY)
      localStorage.removeItem(TENANT_SOURCE_KEY)
      setCustomerId(undefined)
      setTenantSource(undefined)
      if (options?.markChosen) markChosenCompany()
    },
    [markChosenCompany]
  )

  const TenantContextValue = useMemo(
    () => ({
      tenantName: tenant?.name,
      companyName: company?.name,
      customerId,
      tenantCid: tenant?.cid,
      tenantSource,
      hasChosenCompany,
      setCustomerIdHandler,
      removeCustomerIdHandler,
    }),
    [
      tenant,
      company?.name,
      customerId,
      tenantSource,
      hasChosenCompany,
      setCustomerIdHandler,
      removeCustomerIdHandler,
    ]
  )

  return (
    <TenantContext.Provider value={TenantContextValue}>
      {children}
    </TenantContext.Provider>
  )
}

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext)

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
